/**
 * useChecklistTemplates - Hooks do Template de Checklist
 *
 * Todo acesso passa pelas RPCs do módulo (frontend nunca lê tabelas direto).
 * Permissões e tenant são validados no banco (has_granular_permission).
 *
 * @version 1.2.0
 * RELEASE: 2026-09-28 v1.2.0 - Upload privado e tipagem da foto modelo dos itens.
 * RELEASE: 2026-09-26 v1.1.0 - Listagem passa a consumir o nome do Tipo de Checklist.
 * RELEASE: 2026-09-26 v1.0.0 - Criação inicial (Fase 1b)
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useChecklistTemplates');

// -----------------------------------------------------------------------------
// Tipos
// -----------------------------------------------------------------------------
export type ChecklistFieldType =
  | 'text' | 'number' | 'yes_no' | 'single_select' | 'multi_select'
  | 'date' | 'time' | 'datetime' | 'file' | 'photo' | 'photo_or_file';

/** Rótulos em português dos tipos de campo (ordem da referência do usuário) */
export const CHECKLIST_FIELD_TYPES: { value: ChecklistFieldType; label: string }[] = [
  { value: 'text', label: 'Texto' },
  { value: 'number', label: 'Número' },
  { value: 'yes_no', label: 'Sim/Não' },
  { value: 'single_select', label: 'Seleção Única' },
  { value: 'multi_select', label: 'Seleção Múltipla' },
  { value: 'date', label: 'Data' },
  { value: 'time', label: 'Hora' },
  { value: 'datetime', label: 'Data e Hora' },
  { value: 'file', label: 'Arquivo' },
  { value: 'photo', label: 'Foto' },
  { value: 'photo_or_file', label: 'Foto ou Arquivo' },
];

export const fieldTypeLabel = (t: string) =>
  CHECKLIST_FIELD_TYPES.find((f) => f.value === t)?.label ?? t;

export interface ChecklistTemplateListRow {
  id: string;
  name: string;
  /** Nome do Tipo de Checklist vinculado ao modelo. */
  type_name: string | null;
  description: string | null;
  usage_key: string;
  color: string | null;
  icon: string | null;
  status: 'active' | 'inactive';
  has_unpublished_changes: boolean;
  ever_published: boolean;
  current_version_number: number | null;
  sections_count: number;
  items_count: number;
  updated_at: string;
}

export interface ChecklistTemplate {
  id: string;
  name: string;
  /** Tipo de Checklist (obrigatório). */
  type_id: string | null;
  description: string | null;
  usage_key: string;
  status: 'active' | 'inactive';
  has_unpublished_changes: boolean;
  ever_published: boolean;
  current_version_number: number | null;
  deadline_mode: 'free' | 'relative' | 'fixed';
  start_within_min: number | null;
  finish_within_min: number | null;
  allow_overdue: boolean;
  requires_review: boolean;
  allow_self_review: boolean;
  require_photo_on_reject: boolean;
  geo_mode: 'off' | 'submit' | 'item' | 'item_and_submit';
  geo_required: boolean;
  geo_max_radius_m: number | null;
  /** Validade padrão (dias) do link externo da vistoria — independente do prazo da vistoria. */
  external_link_validity_days?: number;
}

export interface ChecklistSection {
  id: string;
  name: string;
  description: string | null;
  sort_order: number;
  is_active: boolean;
  ever_published: boolean;
}

export interface ChecklistItem {
  id: string;
  section_id: string | null;
  stable_key: string;
  name: string;
  description: string | null;
  field_type: ChecklistFieldType;
  is_required: boolean;
  placeholder: string | null;
  default_value: unknown;
  config: Record<string, unknown>;
  requires_approval: boolean;
  is_active: boolean;
  ever_published: boolean;
  sort_order: number;
}

/** Metadados congelados da foto usada como orientação para o vistoriador. */
export interface ChecklistReferencePhoto {
  storage_path: string;
  file_name: string;
  mime_type: 'image/jpeg' | 'image/png' | 'image/webp';
  size_bytes: number;
}

/** Envia uma foto modelo para o caminho privado e exclusivo do template. */
export async function uploadChecklistReferencePhoto(tenantId: string, templateId: string, file: File): Promise<ChecklistReferencePhoto> {
  const safeName = file.name.replace(/[^\w.\-]+/g, '_').slice(-120) || 'foto-modelo';
  const path = `${tenantId}/template-models/${templateId}/${crypto.randomUUID()}-${safeName}`;
  const { error } = await supabase.storage.from('checklists').upload(path, file, {
    upsert: false,
    contentType: file.type,
  });
  if (error) throw new Error(error.message);
  return { storage_path: path, file_name: file.name, mime_type: file.type as ChecklistReferencePhoto['mime_type'], size_bytes: file.size };
}

/** Cria uma URL temporária para visualizar uma foto modelo privada. */
export async function signedChecklistReferencePhoto(path: string) {
  const { data, error } = await supabase.storage.from('checklists').createSignedUrl(path, 600);
  if (error) throw new Error(error.message);
  return data.signedUrl;
}

export interface ChecklistPublishPreview {
  current_version_number: number | null;
  next_version_number: number;
  items_active: number;
  items_added: number;
  items_removed: number;
  items_changed: number;
  has_unpublished_changes: boolean;
  errors: string[];
}

// -----------------------------------------------------------------------------
// Helper: chamada RPC com erro padronizado
// -----------------------------------------------------------------------------
async function callRpc<T>(fn: string, args: Record<string, unknown>): Promise<T> {
  const { data, error } = await (supabase.rpc as any)(fn, args);
  if (error) {
    logger.error(`Erro na RPC ${fn}`, { error: error.message });
    throw new Error(error.message);
  }
  return data as T;
}

// -----------------------------------------------------------------------------
// Listagem
// -----------------------------------------------------------------------------
export function useChecklistTemplatesList(filters: { search?: string; status?: string }) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: ['checklist-templates', tenantId, filters],
    enabled: !!tenantId,
    queryFn: () =>
      callRpc<ChecklistTemplateListRow[]>('list_checklist_templates', {
        p_search: filters.search?.trim() || null,
        p_status: filters.status && filters.status !== 'all' ? filters.status : null,
        p_usage_key: null,
        p_tenant_hint: tenantId ?? null,
      }),
  });
}

// -----------------------------------------------------------------------------
// Detalhe + todas as mutations do editor
// -----------------------------------------------------------------------------
export function useChecklistTemplate(templateId: string | undefined) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;
  const qc = useQueryClient();
  const key = ['checklist-template', templateId];

  const query = useQuery({
    queryKey: key,
    enabled: !!templateId && !!tenantId,
    queryFn: () =>
      callRpc<{ template: ChecklistTemplate; sections: ChecklistSection[]; items: ChecklistItem[] }>(
        'get_checklist_template',
        { p_template_id: templateId, p_tenant_hint: tenantId },
      ),
  });

  /** Invalida o detalhe, a listagem, versões e timeline */
  const refresh = () => {
    qc.invalidateQueries({ queryKey: key });
    qc.invalidateQueries({ queryKey: ['checklist-templates'] });
    qc.invalidateQueries({ queryKey: ['checklist-versions', templateId] });
    qc.invalidateQueries({ queryKey: ['checklist-timeline', templateId] });
  };

  /** Fábrica de mutations com toast de erro padrão */
  const mk = <A,>(fn: (a: A) => Promise<unknown>, success?: string) =>
    useMutation({
      mutationFn: fn,
      onSuccess: () => {
        refresh();
        if (success) toast.success(success);
      },
      onError: (e: Error) => toast.error(e.message),
    });

  const saveTemplate = mk((data: Record<string, unknown>) =>
    callRpc('upsert_checklist_template', { p_data: data, p_template_id: templateId, p_tenant_hint: tenantId }),
    'Modelo salvo');
  const setStatus = mk((status: 'active' | 'inactive') =>
    callRpc('set_checklist_template_status', { p_template_id: templateId, p_status: status, p_tenant_hint: tenantId }),
    'Status atualizado');
  const saveSection = mk((a: { id?: string; name: string; description?: string | null }) =>
    callRpc('upsert_checklist_section', {
      p_template_id: templateId, p_name: a.name, p_description: a.description ?? null,
      p_section_id: a.id ?? null, p_tenant_hint: tenantId,
    }));
  const setSectionActive = mk((a: { id: string; active: boolean }) =>
    callRpc('set_checklist_section_active', { p_section_id: a.id, p_is_active: a.active, p_tenant_hint: tenantId }));
  const deleteSection = mk((id: string) =>
    callRpc('delete_checklist_section', { p_section_id: id, p_tenant_hint: tenantId }), 'Seção excluída');
  const reorderSections = mk((ids: string[]) =>
    callRpc('reorder_checklist_sections', { p_template_id: templateId, p_section_ids: ids, p_tenant_hint: tenantId }));
  const saveItem = mk((a: { id?: string; data: Record<string, unknown> }) =>
    callRpc('upsert_checklist_item', { p_template_id: templateId, p_data: a.data, p_item_id: a.id ?? null, p_tenant_hint: tenantId }),
    'Item salvo');
  const setItemActive = mk((a: { id: string; active: boolean }) =>
    callRpc('set_checklist_item_active', { p_item_id: a.id, p_is_active: a.active, p_tenant_hint: tenantId }));
  const deleteItem = mk((id: string) =>
    callRpc('delete_checklist_item', { p_item_id: id, p_tenant_hint: tenantId }), 'Item excluído');
  const reorderItems = mk((items: { id: string; section_id: string | null; sort_order: number }[]) =>
    callRpc('reorder_checklist_items', { p_template_id: templateId, p_items: items, p_tenant_hint: tenantId }));
  const publish = mk(() =>
    callRpc('publish_checklist_template_version', { p_template_id: templateId, p_tenant_hint: tenantId }),
    'Nova versão publicada');

  return {
    ...query,
    saveTemplate, setStatus, saveSection, setSectionActive, deleteSection, reorderSections,
    saveItem, setItemActive, deleteItem, reorderItems, publish,
  };
}

/** Criação de modelo (fora do editor) */
export function useCreateChecklistTemplate() {
  const { currentTenant } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      callRpc<string>('upsert_checklist_template', { p_data: data, p_template_id: null, p_tenant_hint: currentTenant?.id ?? null }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['checklist-templates'] }),
    onError: (e: Error) => toast.error(e.message),
  });
}

/** Duplicar e excluir modelo (listagem) */
export function useChecklistTemplateActions() {
  const { currentTenant } = useAuth();
  const qc = useQueryClient();
  const tenantHint = currentTenant?.id ?? null;
  const done = () => qc.invalidateQueries({ queryKey: ['checklist-templates'] });
  const duplicate = useMutation({
    mutationFn: (a: { id: string; name: string }) =>
      callRpc<string>('duplicate_checklist_template', { p_template_id: a.id, p_new_name: a.name, p_tenant_hint: tenantHint }),
    onSuccess: () => { done(); toast.success('Modelo duplicado'); },
    onError: (e: Error) => toast.error(e.message),
  });
  const remove = useMutation({
    mutationFn: (id: string) => callRpc('delete_checklist_template', { p_template_id: id, p_tenant_hint: tenantHint }),
    onSuccess: () => { done(); toast.success('Modelo excluído'); },
    onError: (e: Error) => toast.error(e.message),
  });
  const setStatus = useMutation({
    mutationFn: (a: { id: string; status: 'active' | 'inactive' }) =>
      callRpc('set_checklist_template_status', { p_template_id: a.id, p_status: a.status, p_tenant_hint: tenantHint }),
    onSuccess: () => { done(); toast.success('Status atualizado'); },
    onError: (e: Error) => toast.error(e.message),
  });
  return { duplicate, remove, setStatus };
}

/** Prévia da publicação */
export function useChecklistPublishPreview(templateId: string | undefined, open: boolean) {
  const { currentTenant } = useAuth();
  return useQuery({
    queryKey: ['checklist-publish-preview', templateId],
    enabled: open && !!templateId && !!currentTenant?.id,
    staleTime: 0,
    queryFn: () =>
      callRpc<ChecklistPublishPreview>('preview_checklist_publish', {
        p_template_id: templateId, p_tenant_hint: currentTenant?.id ?? null,
      }),
  });
}

/** Histórico de versões */
export function useChecklistVersions(templateId: string | undefined, enabled: boolean) {
  const { currentTenant } = useAuth();
  return useQuery({
    queryKey: ['checklist-versions', templateId],
    enabled: enabled && !!templateId && !!currentTenant?.id,
    queryFn: () =>
      callRpc<{ id: string; version_number: number; change_summary: Record<string, number>; items_count: number; published_by_name: string | null; published_at: string; is_current: boolean }[]>(
        'list_checklist_template_versions', { p_template_id: templateId, p_tenant_hint: currentTenant?.id ?? null }),
  });
}

/** Timeline (eventos) do modelo */
export function useChecklistTimeline(templateId: string | undefined, enabled: boolean) {
  const { currentTenant } = useAuth();
  return useQuery({
    queryKey: ['checklist-timeline', templateId],
    enabled: enabled && !!templateId && !!currentTenant?.id,
    queryFn: () =>
      callRpc<{ id: string; event_type: string; actor_full_name: string | null; metadata: Record<string, unknown>; created_at: string }[]>(
        'get_checklist_timeline', { p_template_id: templateId, p_instance_id: null, p_limit: 200, p_tenant_hint: currentTenant?.id ?? null }),
  });
}
